import { useEffect, useId, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { useSnackbar } from "notistack";
import {
  add_new_feed,
  update_feed,
  update_feed_image_on_s3,
} from "src/DAL/Community/Community";
import GrEmojiPicker from "src/design-system/components/GrEmojiPicker";
import { insertAtCursor } from "src/utils/insertAtCursor";
import GrDialog from "src/design-system/components/GrDialog";
import { s3baseUrl } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrAttachments from "src/design-system/components/GrAttachments";
import GrButton from "src/design-system/components/GrButton";
import GrFilterChips from "src/design-system/components/GrFilterChips";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrTextField from "src/design-system/components/GrTextField";

// Create / edit post popup (community area, post popup, post page, My Posts): the member, the text
// (with emoji), photo / video chips, the photos (max 4) or the video URL, and Post / Update.
const MAX_IMAGES = 4;
const POST_DETAIL_PATH = "/community-area-post-detail/";
const POST_TYPES = [
  { value: "image", label: <GrIcon icon={icons.image} title="Photo" /> },
  { value: "video", label: <GrIcon icon={icons.video} title="Video" /> },
];

const formSx = { display: "grid", gap: gr.space[4], mt: gr.space[2] };
const authorSx = { display: "flex", alignItems: "center", gap: gr.space[3] };
const authorNameSx = {
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink,
  overflowWrap: "anywhere",
};
const toolbarSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[2],
};

export default function CreatePost({
  openCreateModal,
  setOpenCreateModal,
  setFeedType,
  feedType,
  getFeedsListing,
  userInfo,
  formAction,
  selectedFeed,
  setFormAction,
  handleSuccessUpdate,
}) {
  const { selectedGroup } = useContentSetting();
  const { enqueueSnackbar } = useSnackbar();
  const formId = `create-post-${useId()}`;
  const [previews, setPreviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [feedId, setFeedId] = useState("");
  const inputRef = useRef(null);
  const [inputs, setInputs] = useState({
    feedAppearBy: "public",
    VideoUrl: "",
    description: "",
    LiveUrl: "",
  });

  const url = selectedGroup
    ? `api/feed/group/feed_list?limit=15&page=0&group=${selectedGroup}`
    : `api/feed/group/feed_list?limit=15&page=0`;

  const authorName = userInfo?.last_name
    ? `${userInfo.first_name} ${userInfo.last_name}`
    : "";

  // Photo tiles: a photo already on the post shows its S3 thumbnail, a picked file a local preview.
  // Each picked file keeps one preview URL while it is in the list (stable tile key, no re-decoding);
  // the URL is released when the file is removed or the popup unmounts.
  const fileUrlsRef = useRef(new Map());
  const photoTiles = useMemo(() => {
    const urls = new Map();
    const tiles = previews.map((preview) => {
      if (preview.type !== "file") {
        const src = s3baseUrl + preview.thumbnail_1;
        return { key: src, src, image: true };
      }
      const src =
        fileUrlsRef.current.get(preview.path) ||
        URL.createObjectURL(preview.path);
      urls.set(preview.path, src);
      return { key: src, src, image: true };
    });
    fileUrlsRef.current.forEach(
      (src, file) => !urls.has(file) && URL.revokeObjectURL(src)
    );
    fileUrlsRef.current = urls;
    return tiles;
  }, [previews]);
  useEffect(() => {
    const fileUrls = fileUrlsRef;
    return () => fileUrls.current.forEach((src) => URL.revokeObjectURL(src));
  }, []);

  const handleRemoveImage = (path) => {
    setPreviews((images) => {
      return images.filter((image) => image !== path);
    });
  };

  // A photo that fails to upload stops the post (with the server's message): before, the post was
  // saved as a photo post with no photo in it (an empty post in the feed).
  const UploadImages = async (formData) => {
    const result = await update_feed_image_on_s3(formData);
    if (!result?.images_obj) throw new Error(result?.message);
    return result.images_obj;
  };

  // New photos are uploaded first; photos already on the post are kept as they are.
  const uploadPhotos = () =>
    Promise.all(
      previews.map((image) => {
        if (image.type !== "file") return image;
        const formData = new FormData();
        formData.append("images", image.path);
        return UploadImages(formData);
      })
    );

  // The post's text, photos and video, the same for a new and an edited post. The video URL is sent
  // only for a video post, so a URL typed and then cleared (× or the Video chip) is not saved.
  const appendPostContent = (formData, imageResults) => {
    const videoUrl = feedType === "video" ? inputs.VideoUrl : "";
    formData.append("video_url", videoUrl);
    formData.append("description", inputs.description);
    if (feedType === "image" && previews.length >= 1) {
      formData.append("image", JSON.stringify(imageResults));
      formData.append("feed_type", "image");
    } else {
      formData.append("feed_type", videoUrl ? "video" : "general");
    }
  };

  const handleUpload = async (event) => {
    const fileList = event.target.files;
    const imagesToAdd = [];

    if (previews.length + fileList.length > MAX_IMAGES) {
      const remainingSlots = MAX_IMAGES - previews.length;
      for (let i = 0; i < remainingSlots; i++) {
        imagesToAdd.push({
          path: fileList[i],
          type: "file",
        });
      }
    } else {
      for (let i = 0; i < fileList.length; i++) {
        imagesToAdd.push({
          path: fileList[i],
          type: "file",
        });
      }
    }
    setPreviews((prevFiles) => [...prevFiles, ...imagesToAdd]);
  };

  const handleClose = () => {
    setOpenCreateModal(false);
    setInputs({
      feedAppearBy: "public",
      VideoUrl: "",
      description: "",
    });
    setPreviews([]);
    setFormAction("ADD");
  };

  const validateFeed = () => {
    if (feedType === "general" && !inputs.description?.trim()) {
      enqueueSnackbar("Description is required", {
        variant: "error",
      });
      return false;
    }
    if (feedType === "image" && previews.length < 1) {
      enqueueSnackbar("Please upload at least one image", {
        variant: "error",
      });
      return false;
    }
    if (feedType === "video" && !inputs.VideoUrl?.trim()) {
      enqueueSnackbar("Video URL is required for a video post", {
        variant: "error",
      });
      return false;
    }
    return true;
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!validateFeed()) return;

    // "Posting..." ends even when a photo upload fails (it used to stay on for good).
    setIsLoading(true);
    try {
      const img_results = await uploadPhotos();
      const formData = new FormData();
      formData.append("created_for", selectedGroup ? "group" : "general");
      if (selectedGroup) {
        formData.append("community_group", selectedGroup);
      }
      appendPostContent(formData, img_results);

      const result = await add_new_feed(formData);
      if (result.code === 200) {
        setInputs({
          feedAppearBy: "public",
          VideoUrl: "",
          description: "",
        });
        getFeedsListing(url);
        handleClose();
        enqueueSnackbar(result.message, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    } catch (error) {
      if (error?.message) enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };
  const handleChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleCancelUpdate = (e) => {
    e.preventDefault();
    handleClose();
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();

    if (!validateFeed()) return;
    // "Updating..." on the button while the photos upload and the post saves (it never showed before).
    setIsLoading(true);
    try {
      const img_results = await uploadPhotos();
      const formData = new FormData();
      appendPostContent(formData, img_results);
      const result = await update_feed(formData, feedId);
      if (result.code === 200) {
        setInputs({
          feedAppearBy: "public",
          VideoUrl: "",
          description: "",
        });

        // On the post's own page the post itself is refreshed; elsewhere the feed list.
        if (!window.location.pathname.startsWith(POST_DETAIL_PATH)) {
          getFeedsListing(url);
        } else {
          getFeedsListing(feedId);
        }
        handleClose();
        enqueueSnackbar(result.message, {
          variant: "success",
        });
        // Optional: My Posts reloads its list through getFeedsListing alone (it loaded twice before).
        if (handleSuccessUpdate) handleSuccessUpdate(selectedFeed);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    } catch (error) {
      if (error?.message) enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formAction === "EDIT") {
      setInputs({
        VideoUrl: selectedFeed.video_url,
        description: selectedFeed.description,
      });
      setFeedType(selectedFeed.feed_type);
      setFeedId(selectedFeed._id);
      if (
        selectedFeed.feed_type === "image" &&
        selectedFeed.image &&
        selectedFeed.image.length > 0
      ) {
        setPreviews(selectedFeed.image);
      }
    }
  }, [formAction]);

  // Photo / video chips: picking the selected one again goes back to a text post (the old × on the
  // photo box and the video field did the same).
  const handlePostType = (type) =>
    setFeedType(type === feedType ? "general" : type);

  const isEdit = formAction === "EDIT";

  return (
    <GrDialog
      showClose
      align="top"
      open={Boolean(openCreateModal)}
      onClose={handleClose}
      title={`${formAction === "ADD" ? "Create" : "Update"} post`}
      actions={
        isEdit ? (
          <>
            <GrButton variant="quiet" onClick={handleCancelUpdate}>
              Cancel
            </GrButton>
            <GrButton type="submit" form={formId} loading={isLoading}>
              {isLoading ? "Updating..." : "Update"}
            </GrButton>
          </>
        ) : (
          <GrButton type="submit" form={formId} loading={isLoading}>
            {isLoading ? "Posting..." : "Post"}
          </GrButton>
        )
      }
    >
      <Box
        component="form"
        id={formId}
        onSubmit={isEdit ? handleUpdatePost : handleCreatePost}
        sx={formSx}
      >
        <Box sx={authorSx}>
          <GrAvatar
            src={s3baseUrl + userInfo?.profile_image}
            name={authorName}
          />
          <Box component="span" sx={authorNameSx}>
            {authorName}
          </Box>
        </Box>

        <GrTextField
          inputRef={inputRef}
          placeholder="What's on your mind?"
          inputProps={{ "aria-label": "What's on your mind?" }}
          multiline
          minRows={2}
          maxRows={6}
          name="description"
          value={inputs.description}
          onChange={handleChange}
          autoFocus
        />

        <Box sx={toolbarSx}>
          <GrFilterChips
            ariaLabel="Post type"
            value={feedType}
            onChange={handlePostType}
            options={POST_TYPES}
          />
          <GrEmojiPicker
            onPick={(emoji) =>
              setInputs((values) => ({
                ...values,
                description: insertAtCursor(inputRef.current, emoji),
              }))
            }
          />
        </Box>

        {feedType === "video" && (
          <GrTextField
            placeholder="Video URL"
            inputProps={{ "aria-label": "Video URL" }}
            name="VideoUrl"
            value={inputs.VideoUrl}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <GrIconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Clear video URL"
                    onClick={() => setFeedType("general")}
                  >
                    <GrIcon icon={icons.close} />
                  </GrIconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {feedType === "image" && (
          <GrAttachments
            files={photoTiles}
            accept="image/*"
            onRemove={(index) => handleRemoveImage(previews[index])}
            onAdd={previews.length < MAX_IMAGES ? handleUpload : undefined}
            hint={
              previews.length < MAX_IMAGES
                ? "Maximum 4 images are allowed per upload."
                : undefined
            }
            addLabel="Add Photo"
          />
        )}
      </Box>
    </GrDialog>
  );
}
